import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@/api/errors'
import { cn } from '@/lib/cn'
import { IconClose, IconError, IconInfo, IconSuccess, IconWarning } from '@/icons'

/**
 * The one notification system (best practices §1.6).
 *
 * Silent failure is a bug, so every mutation in this app ends in a toast — success or refusal.
 * A feature never builds its own.
 */

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  readonly id: string
  readonly tone: ToastTone
  readonly message: string
  /** A second line, usually the server's `detail`. */
  readonly description?: string
}

interface ToastContextValue {
  show: (toast: Omit<Toast, 'id'>) => void
  success: (message: string, description?: string) => void
  error: (message: string, description?: string) => void
  /** Turns any thrown value into the right toast, so a `catch` is one line everywhere. */
  fromError: (cause: unknown, fallback: string) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 5000,
  warning: 7000,
  // An error stays until it is dismissed. A person who missed it cannot ask for it back.
  error: Infinity,
}

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      nextId += 1
      const id = `toast-${String(nextId)}`
      setToasts((current) => [...current, { ...toast, id }])

      const duration = DURATION[toast.tone]
      if (Number.isFinite(duration)) {
        timers.current.set(
          id,
          setTimeout(() => {
            dismiss(id)
          }, duration),
        )
      }
    },
    [dismiss],
  )

  // Clear every pending timer on unmount, or a fired callback would set state on a dead tree.
  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const timer of pending.values()) clearTimeout(timer)
      pending.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      dismiss,
      success: (message, description) => {
        show({ tone: 'success', message, ...(description ? { description } : {}) })
      },
      error: (message, description) => {
        show({ tone: 'error', message, ...(description ? { description } : {}) })
      },
      fromError: (cause, fallback) => {
        // The server's `detail` is written for a person and is more useful than anything
        // generic we could substitute, so it becomes the second line when present.
        const description = isApiError(cause) ? cause.detail : undefined
        show({ tone: 'error', message: fallback, ...(description ? { description } : {}) })
      },
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside a ToastProvider')
  return context
}

const TONE_STYLES: Record<ToastTone, { border: string; icon: string }> = {
  success: { border: 'border-l-success', icon: 'text-success' },
  error: { border: 'border-l-danger', icon: 'text-danger' },
  warning: { border: 'border-l-warning', icon: 'text-warning' },
  info: { border: 'border-l-info', icon: 'text-info' },
}

const TONE_ICONS: Record<ToastTone, typeof IconSuccess> = {
  success: IconSuccess,
  error: IconError,
  warning: IconWarning,
  info: IconInfo,
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: readonly Toast[]
  onDismiss: (id: string) => void
}) {
  return (
    /*
     * Two live regions, because politeness differs by tone: an error interrupts, a success
     * waits its turn. One region cannot be both.
     */
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end">
      <div aria-live="assertive" aria-atomic="false" className="contents">
        {toasts
          .filter((toast) => toast.tone === 'error' || toast.tone === 'warning')
          .map((toast) => (
            <ToastRow key={toast.id} toast={toast} onDismiss={onDismiss} />
          ))}
      </div>
      <div aria-live="polite" aria-atomic="false" className="contents">
        {toasts
          .filter((toast) => toast.tone === 'success' || toast.tone === 'info')
          .map((toast) => (
            <ToastRow key={toast.id} toast={toast} onDismiss={onDismiss} />
          ))}
      </div>
    </div>
  )
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { t } = useTranslation()
  const Icon = TONE_ICONS[toast.tone]
  const styles = TONE_STYLES[toast.tone]

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-2.5',
        'rounded-control border border-l-3 border-border bg-overlay p-3 shadow-overlay',
        'motion-safe:animate-in motion-safe:slide-in-from-bottom-2 motion-safe:fade-in',
        styles.border,
      )}
    >
      <Icon size={16} className={cn('mt-0.5 shrink-0', styles.icon)} />
      <div className="min-w-0 flex-1">
        <p className="text-[0.8125rem] leading-5 font-medium text-text">{toast.message}</p>
        {toast.description ? (
          <p className="mt-0.5 text-xs leading-4 break-words text-text-secondary">
            {toast.description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => {
          onDismiss(toast.id)
        }}
        aria-label={t('common.close')}
        className="-m-1 shrink-0 rounded p-1 text-text-muted transition-colors hover:text-text"
      >
        <IconClose size={14} />
      </button>
    </div>
  )
}
